import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Stack,
  Chip,
  capitalize,
  Autocomplete,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Collapse,
  Button,
} from '@mui/material';
import { Check, Close, RemoveRedEyeOutlined, FilterList, Clear, Assignment, TrendingUp, Schedule, Assessment } from '@mui/icons-material';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import { useNavigate } from 'react-router-dom';
import OnGeneralMessageDialog from '../../../../components/Dialogs/OnGeneralMessageDialog';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';
import AccessDeniedBox from '../../../../components/AccessDeniedBox';
import EmptyState from '../../../../components/EmptyState';
import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);


function EstadoChip({ estado }) {
  let color = 'default';
  if (estado === 'aceptada') color = 'success';
  else if (estado === 'abierta') color = 'warning';
  else if (estado === 'rechazada') color = 'error';
  return <Chip label={capitalize(estado)} color={color} textTransform="none" size="small"/>;
}

const Solicitudes = ({ onLoanCreated }) => {
  const navigate = useNavigate();
  const [solicitudes, setSolicitudes] = useState([]);
  const [solicitudesFiltered, setSolicitudesFiltered] = useState([]);
  const [openGeneralAcceptMessageDialog, setOpenGeneralAcceptMessageDialog] = useState(false);
  const [openGeneralRejectMessageDialog, setOpenGeneralRejectMessageDialog] = useState(false);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [search, setSearch] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    estado: '',
    producto: '',
    montoMin: '',
    montoMax: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [productos, setProductos] = useState([]);

  const handleInputChange = (val) => {
    if(val != null) {
      setSolicitudesFiltered(Array.isArray(solicitudes) ? solicitudes.filter(solicitud => solicitud.id==val.id) : []);
    } else {
      setSolicitudesFiltered(Array.isArray(solicitudes) ? solicitudes : []);
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }

  const applyFilters = () => {
    let filtered = [...solicitudes];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(solicitud => 
        JSON.parse(solicitud.campos)[1]?.toLowerCase().includes(searchTerm) ||
        solicitud.producto_nombre?.toLowerCase().includes(searchTerm)
      );
    }

    // Estado filter
    if (filters.estado) {
      filtered = filtered.filter(solicitud => solicitud.estado === filters.estado);
    }

    // Producto filter
    if (filters.producto) {
      filtered = filtered.filter(solicitud => solicitud.producto_nombre === filters.producto);
    }

    // Monto filters
    if (filters.montoMin) {
      filtered = filtered.filter(solicitud => parseFloat(JSON.parse(solicitud.campos)[10]) >= parseFloat(filters.montoMin));
    }
    if (filters.montoMax) {
      filtered = filtered.filter(solicitud => parseFloat(JSON.parse(solicitud.campos)[10]) <= parseFloat(filters.montoMax));
    }

    // Date filters
    if (filters.fechaDesde) {
      filtered = filtered.filter(solicitud => {
        const fechaSolicitud = new Date(solicitud.fecha_solicitud.split('/').reverse().join('-'));
        const fechaDesde = new Date(filters.fechaDesde);
        return fechaSolicitud >= fechaDesde;
      });
    }
    if (filters.fechaHasta) {
      filtered = filtered.filter(solicitud => {
        const fechaSolicitud = new Date(solicitud.fecha_solicitud.split('/').reverse().join('-'));
        const fechaHasta = new Date(filters.fechaHasta);
        return fechaSolicitud <= fechaHasta;
      });
    }

    setSolicitudesFiltered(Array.isArray(filtered) ? filtered : []);
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      estado: '',
      producto: '',
      montoMin: '',
      montoMax: '',
      fechaDesde: '',
      fechaHasta: ''
    });
    setSolicitudesFiltered(Array.isArray(solicitudes) ? solicitudes : []);
  }

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [filters, solicitudes]);

  useEffect(() => {
      const fetchSolicitudes = async () => {
          try {
              const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/solicitudes-prestamo/${localStorage.getItem('idcompany')}`,{
                  headers: {
                      'Content-Type': 'application/json',
                      'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
                  }
              });

              if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
              }

              const data = await response.json();
              if(data["error"]!="Acceso denegado"){
                setAccessDenied(false);
                // Ensure data is always an array
                const solicitudesData = Array.isArray(data) ? data : (data.solicitudes || []);
                setSolicitudes(solicitudesData);
                setSolicitudesFiltered(solicitudesData);
                
                // Extract unique productos for filter
                  const uniqueProductos = [...new Set(solicitudesData?.length > 0 ? solicitudesData.map(item => item.producto_nombre).filter(Boolean) : [] )];
                  setProductos(uniqueProductos);
              } else {
                setAccessDenied(true);
              }
          } catch (error) {
              console.error('Error fetching solicitudes:', error);
              // Set empty arrays to prevent iteration errors
              setSolicitudes([]);
              setSolicitudesFiltered([]);
              setProductos([]);
          }
      };
      fetchSolicitudes();
  }, []);

  const handleAcceptSolicitud = async() => {
    setOpenGeneralAcceptMessageDialog(false);     

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/solicitudes-prestamo/${solicitudSeleccionada.id}`,{
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          estado: 'aceptada',
          idcompany: localStorage.getItem('idcompany'),
          id_sucursal: localStorage.getItem('id_sucursal'),
          monto_solicitado: JSON.parse(solicitudSeleccionada["campos"])[10],
          monto_inicial: JSON.parse(solicitudSeleccionada["campos"])[11],
          capital: JSON.parse(solicitudSeleccionada["campos"])[12],
          interes: JSON.parse(solicitudSeleccionada["campos"])[13],
        })
      });
      if (!response.ok) {
        const error = await response.json();
        console.log(error)
        return;
      }
      const data = await response.json();
      console.log(data);
      var url = `${process.env.REACT_APP_SERVER_URL}/api/prestamos`;
      var dataPrestamo = {
        "idcompany": localStorage.getItem('idcompany'),
        "id_solicitud": solicitudSeleccionada.id,
        "id_sucursal": localStorage.getItem('id_sucursal'),
        "id_cliente": solicitudSeleccionada.id_cliente,
        "monto_solicitado": JSON.parse(solicitudSeleccionada["campos"])[10],
        "monto_inicial": JSON.parse(solicitudSeleccionada["campos"])[11],
        "interes": JSON.parse(solicitudSeleccionada["campos"])[13],
        "mora": JSON.parse(solicitudSeleccionada["campos"])[14],
        "frecuencia": JSON.parse(solicitudSeleccionada["campos"])[12],
        "metodo": JSON.parse(solicitudSeleccionada.interes_detalle).tipoInteres,
        "plazo": JSON.parse(solicitudSeleccionada["campos"])[11],
        "producto":solicitudSeleccionada.requiereInventario?JSON.parse(solicitudSeleccionada["campos"])["id"]:"no_requiere_inventario",
        "precio_venta": solicitudSeleccionada.requiereInventario?JSON.parse(solicitudSeleccionada["campos"])["precio_venta"]:0,
        "fecha_inicio": dayjs.utc(solicitudSeleccionada.fecha_inicio_prestamo).format('YYYY-MM-DD'),
        "mora_porcentaje": JSON.parse(solicitudSeleccionada.mora_detalle).esPorcentaje ? JSON.parse(solicitudSeleccionada.mora_detalle).valorMora : 0.00,
        "created_by": localStorage.getItem('id')
      
      }
      var responsePrestamo = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify(dataPrestamo )
      }); 
      if (!responsePrestamo.ok) {
        const error = await responsePrestamo.json();
        console.log(error)
        return;
      } 
//      const dataPrestamo = await responsePrestamo.json();
//      console.log(dataPrestamo);

      setOpenAlert(true);
      setSolicitudes(Array.isArray(solicitudes) && solicitudes.length > 0 ? solicitudes.map(solicitud => solicitud.id === solicitudSeleccionada.id ? {...solicitud, estado: 'aceptada'} : solicitud) : []);
      
      // Refresh loans list in parent component
      if (onLoanCreated) {
        onLoanCreated();
      }
    } catch (error) {
      console.log(error);
    }
    




  }

  const handleRejectSolicitud = async() => {
    setOpenGeneralRejectMessageDialog(false);
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/solicitudes-prestamo/${solicitudSeleccionada.id}`,{
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify({
          estado: 'rechazada'
        })
      });
      if (!response.ok) {
        const error = await response.json();
        console.log(error)
        return;
      }
      const data = await response.json();
      console.log(data);
      setOpenAlert(true);
      setSolicitudes(Array.isArray(solicitudes) && solicitudes.length > 0 ? solicitudes.map(solicitud => solicitud.id === solicitudSeleccionada.id ? {...solicitud, estado: 'rechazada'} : solicitud) : []);
    } catch (error) {
      console.log(error);
    }
  }

  return (
    !accessDenied ? 
    <Box sx={{ p: 3 }}>
      <AlertMessageDialog message={`La solicitud de prestamo se ha aceptado con exito.`} title={''} severity={"success"} color={"success"} open={openAlert} handleClose={()=>{setOpenAlert(false);}}/>
      
      <OnGeneralMessageDialog open={openGeneralAcceptMessageDialog} onClose={()=>{setOpenGeneralAcceptMessageDialog(false)}} title={"Confirmar"} message={"Deseas aceptar la solicitud de prestamo?"} buttonText={"Confirmar"} onRegister={()=>{handleAcceptSolicitud()}}/>
      <OnGeneralMessageDialog open={openGeneralRejectMessageDialog} onClose={()=>{setOpenGeneralRejectMessageDialog(false)}} title={"Confirmar"} message={"Deseas rechazar la solicitud de prestamo?"} buttonText={"Confirmar"} onRegister={()=>{handleRejectSolicitud()}}/>
      
      {/* Filter Controls */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
        <Button 
          variant="outlined" 
          startIcon={<FilterList />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{ fontWeight: "bold" }}
        >
          Filtros
        </Button>
        
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {solicitudesFiltered?.length} de {solicitudes?.length} solicitudes
        </Typography>
      </Stack>

      {/* Filter Panel */}
      <Collapse in={showFilters} sx={{ width: '100%', mb: 2 }}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList color="primary" />
              Filtros de Búsqueda
            </Typography>
            
            <Grid container spacing={2}>
              {/* Estado Filter */}
              <Grid item size={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                    Estado
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={filters.estado}
                      onChange={(e) => handleFilterChange('estado', e.target.value)}
                      sx={{backgroundColor: 'white'}}
                      displayEmpty
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="abierta">Abierta</MenuItem>
                      <MenuItem value="aceptada">Aceptada</MenuItem>
                      <MenuItem value="rechazada">Rechazada</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Producto Filter */}
              <Grid item size={4}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                    Producto Financiero
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={filters.producto}
                      onChange={(e) => handleFilterChange('producto', e.target.value)}
                      sx={{backgroundColor: 'white'}}
                      displayEmpty
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {productos.map((producto) => (
                        <MenuItem key={producto} value={producto}>
                          {producto}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Empty Grid for spacing */}
              <Grid item size={4}>
              </Grid>

              {/* Monto Min */}
              <Grid item size={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                    Monto Mínimo
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    placeholder="0"
                    value={filters.montoMin}
                    onChange={(e) => handleFilterChange('montoMin', e.target.value)}
                    sx={{backgroundColor: 'white'}}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                    }}
                  />
                </Box>
              </Grid>

              {/* Monto Max */}
              <Grid item size={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                    Monto Máximo
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    placeholder="999999"
                    value={filters.montoMax}
                    onChange={(e) => handleFilterChange('montoMax', e.target.value)}
                    sx={{backgroundColor: 'white'}}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                    }}
                  />
                </Box>
              </Grid>

              {/* Fecha Desde */}
              <Grid item size={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                    Fecha Desde
                  </Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={filters.fechaDesde}
                    onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
                    sx={{backgroundColor: 'white'}}
                  />
                </Box>
              </Grid>

              {/* Fecha Hasta */}
              <Grid item size={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                    Fecha Hasta
                  </Typography>
                  <TextField
                    fullWidth
                    type="date"
                    value={filters.fechaHasta}
                    onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
                    sx={{backgroundColor: 'white'}}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Filter Actions */}
            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<Clear />}
                onClick={clearFilters}
                sx={{ fontWeight: "bold" }}
              >
                Limpiar Filtros
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Collapse>

      {/* Quick Search Autocomplete */}
      <Autocomplete
        options={solicitudesFiltered?.length > 0 ? solicitudesFiltered : []}
        getOptionLabel={(option) => {return JSON.parse(option.campos)[1]+" - $"+formatNumberWithCommas(JSON.parse(option.campos)[10].toFixed(2))+" - "+capitalize(option.estado)}}
        renderInput={(params) => <TextField {...params} placeholder="Búsqueda rápida por solicitud específica" fullWidth/>}
        onChange={(e, val) => {handleInputChange(val)}}
        sx={{mb:2}}
      />
      {/* Show empty state if no solicitudes */}
      {solicitudesFiltered?.length === 0 ? (
        <EmptyState 
          icon={Assignment}
          title="No hay solicitudes de préstamo"
          description="No se encontraron solicitudes de préstamo. Los clientes pueden enviar solicitudes que aparecerán aquí para su revisión y aprobación."
          features={[
            {
              icon: TrendingUp,
              title: "Proceso Simplificado",
              description: "Gestión eficiente del flujo de solicitudes"
            },
            {
              icon: Schedule,
              title: "Revisión Rápida",
              description: "Aprobación o rechazo con un solo clic"
            },
            {
              icon: Assessment,
              title: "Seguimiento Completo",
              description: "Historial detallado de todas las solicitudes"
            }
          ]}
          buttonText="Crear Nueva Solicitud"
          onButtonClick={() => navigate('/gestion_comercial/prestamos/crear')}
          helpText="¿Necesitas ayuda? Contacta a soporte técnico para guiarte en el proceso"
        />
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', mt:2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledCellHeader sx={{textAlign: 'left'}}>Cliente</StyledCellHeader>
                <StyledCellHeader sx={{textAlign: 'center'}}>Producto Financiero</StyledCellHeader>
                <StyledCellHeader sx={{textAlign: 'center'}}>Monto Solicitado</StyledCellHeader>
                <StyledCellHeader sx={{textAlign: 'center'}}>Estado</StyledCellHeader>
                <StyledCellHeader sx={{textAlign: 'center'}}>Fecha de Solicitud</StyledCellHeader>
                <StyledCellHeader></StyledCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {solicitudesFiltered?.length > 0 ? solicitudesFiltered.map((solicitud) => (
                <TableRow key={solicitud.id}>
                  <TableCell sx={{textAlign: 'left'}}>{JSON.parse(solicitud.campos)[1]}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>{solicitud.producto_nombre}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>${formatNumberWithCommas(JSON.parse(solicitud.campos)[10].toFixed(2))}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>
                    <EstadoChip estado={solicitud.estado} />
                  </TableCell>
                  <TableCell sx={{textAlign: 'center'}}>{solicitud.fecha_solicitud}</TableCell>
                  <TableCell sx={{textAlign: 'center'}}>
                    <IconButton size="small" onClick={()=>{navigate(`/gestion_comercial/prestamos/solicitudes/detalle/${solicitud.id}`)}}>  
                      <RemoveRedEyeOutlined />
                    </IconButton>
                    <IconButton size="small" sx={{backgroundColor: 'primarybutton.main',  marginLeft: 1}} onClick={()=>{setSolicitudSeleccionada(solicitud);setOpenGeneralAcceptMessageDialog(true)}}  disabled={solicitud.estado === 'aceptada' || solicitud.estado === 'rechazada'}>
                      <Check sx={{color: 'white'}}/>
                    </IconButton>
                    <IconButton size="small" sx={{backgroundColor: 'error.main',  marginLeft: 1}} onClick={()=>{setSolicitudSeleccionada(solicitud);setOpenGeneralRejectMessageDialog(true)}} disabled={solicitud.estado === 'aceptada' || solicitud.estado === 'rechazada'}>
                      <Close sx={{color: 'white'}}/>
                    </IconButton>
                  </TableCell>
                </TableRow>
              )) : []}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
    :
    <AccessDeniedBox
      title={'Acceso denegado'}
      message={'No tienes permisos para ver esta sección de Préstamos. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
    />
  );            
};

export default Solicitudes;
